import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { 
  DollarSign, Save, RotateCcw, Building2, 
  DoorOpen, Grid3X3, Plus, Trash2, Package, Percent
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const PricingPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stylePrices, setStylePrices] = useState([]);
  const [featurePrices, setFeaturePrices] = useState([]);
  const [rates, setRates] = useState({
    manufacturer_discount_percent: 10,
    sales_tax_percent: 7,
    deposit_percent: 17
  });
  const [activeTab, setActiveTab] = useState('styles');
  const [updatedAt, setUpdatedAt] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      setLoading(true);
      const res = await api.get('/pricing');
      setStylePrices(res.data.style_prices || []);
      setFeaturePrices(res.data.feature_prices || []);
      setRates(res.data.rates || {
        manufacturer_discount_percent: 10,
        sales_tax_percent: 7,
        deposit_percent: 17
      });
      setUpdatedAt(res.data.updated_at);
      setUpdatedBy(res.data.updated_by);
    } catch (error) {
      console.error('Failed to fetch pricing:', error);
      toast.error('Failed to load pricing data');
    } finally {
      setLoading(false);
    }
  };

  const handleStyleChange = (index, field, value) => {
    const updated = [...stylePrices];
    updated[index] = { ...updated[index], [field]: field.includes('price') ? parseFloat(value) || 0 : value };
    setStylePrices(updated);
  };

  const handleFeatureChange = (index, field, value) => {
    const updated = [...featurePrices];
    updated[index] = { ...updated[index], [field]: field === 'price' ? parseFloat(value) || 0 : value };
    setFeaturePrices(updated);
  };

  const handleRateChange = (field, value) => {
    setRates(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  };

  const addFeature = () => {
    setFeaturePrices([
      ...featurePrices,
      {
        feature_id: `custom_${Date.now()}`,
        feature_name: 'New Feature',
        category: 'addon',
        price: 0,
        description: ''
      }
    ]);
  };

  const removeFeature = (index) => {
    const updated = featurePrices.filter((_, i) => i !== index);
    setFeaturePrices(updated);
  };

  const savePricing = async () => {
    try {
      setSaving(true);
      await api.put('/pricing', {
        style_prices: stylePrices,
        feature_prices: featurePrices,
        rates: rates
      });
      toast.success('Pricing saved successfully');
      fetchPricing();
    } catch (error) {
      console.error('Failed to save pricing:', error);
      toast.error('Failed to save pricing');
    } finally {
      setSaving(false);
    }
  };

  const resetPricing = async () => {
    if (!window.confirm('Are you sure you want to reset all pricing to defaults?')) return;
    
    try {
      setSaving(true);
      await api.post('/pricing/reset');
      toast.success('Pricing reset to defaults');
      fetchPricing();
    } catch (error) {
      console.error('Failed to reset pricing:', error);
      toast.error('Failed to reset pricing');
    } finally {
      setSaving(false);
    }
  };

  const categoryLabels = {
    door: 'Doors',
    window: 'Windows',
    upgrade: 'Upgrades (per sqft)',
    addon: 'Add-ons',
    service: 'Services'
  };

  const categoryIcons = {
    door: DoorOpen,
    window: Grid3X3,
    upgrade: Building2,
    addon: Package,
    service: Package
  };

  const groupedFeatures = featurePrices.reduce((acc, feature, index) => {
    const cat = feature.category || 'addon';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push({ ...feature, originalIndex: index });
    return acc;
  }, {});

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#C41E3A]"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <DollarSign className="w-7 h-7 text-[#C41E3A]" />
              Retail Pricing
            </h1>
            <p className="text-gray-500 mt-1">
              Manage base prices for building styles and features
            </p>
            {updatedAt && (
              <p className="text-sm text-gray-400 mt-1">
                Last updated: {new Date(updatedAt).toLocaleString()} by {updatedBy}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={resetPricing}
              disabled={saving}
              className="flex items-center gap-2"
            >
              <RotateCcw className="w-4 h-4" />
              Reset to Defaults
            </Button>
            <Button
              onClick={savePricing}
              disabled={saving}
              className="bg-[#C41E3A] hover:bg-[#a01830] text-white flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b">
          <button
            onClick={() => setActiveTab('styles')}
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              activeTab === 'styles'
                ? 'border-[#C41E3A] text-[#C41E3A]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Building2 className="w-4 h-4 inline mr-2" />
            Building Styles
          </button>
          <button
            onClick={() => setActiveTab('features')}
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              activeTab === 'features'
                ? 'border-[#C41E3A] text-[#C41E3A]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Package className="w-4 h-4 inline mr-2" />
            Features & Add-ons
          </button>
          <button
            onClick={() => setActiveTab('rates')}
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              activeTab === 'rates'
                ? 'border-[#C41E3A] text-[#C41E3A]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Percent className="w-4 h-4 inline mr-2" />
            Rates & Discounts
          </button>
        </div>

        {/* Building Styles Tab */}
        {activeTab === 'styles' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-200">Style</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-200">Description</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700 dark:text-gray-200">Base Price ($)</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700 dark:text-gray-200">Price/sqft ($)</th>
                  </tr>
                </thead>
                <tbody>
                  {stylePrices.map((style, index) => (
                    <tr key={style.style_id} className="border-t dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="py-3 px-4">
                        <div className="font-medium text-gray-800 dark:text-white">{style.style_name}</div>
                        <div className="text-xs text-gray-400">{style.style_id}</div>
                      </td>
                      <td className="py-3 px-4">
                        <Input
                          value={style.description || ''}
                          onChange={(e) => handleStyleChange(index, 'description', e.target.value)}
                          className="text-sm"
                          placeholder="Description"
                        />
                      </td>
                      <td className="py-3 px-4">
                        <Input
                          type="number"
                          step="0.01"
                          value={style.base_price}
                          onChange={(e) => handleStyleChange(index, 'base_price', e.target.value)}
                          className="text-right w-28 ml-auto"
                        />
                      </td>
                      <td className="py-3 px-4">
                        <Input
                          type="number"
                          step="0.01"
                          value={style.price_per_sqft}
                          onChange={(e) => handleStyleChange(index, 'price_per_sqft', e.target.value)}
                          className="text-right w-24 ml-auto"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="p-4 bg-gray-50 dark:bg-gray-900 border-t dark:border-gray-700 text-sm text-gray-500 dark:text-gray-400">
              <strong>Formula:</strong> Total Base = Base Price + (Width × Length × Price/sqft)
            </div>
          </div>
        )}

        {/* Features Tab */}
        {activeTab === 'features' && (
          <div className="space-y-6">
            {Object.entries(categoryLabels).map(([category, label]) => {
              const features = groupedFeatures[category] || [];
              const IconComponent = categoryIcons[category];
              
              return (
                <div key={category} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
                  <div className="bg-gray-50 dark:bg-gray-900 px-4 py-3 border-b dark:border-gray-700 flex items-center gap-2">
                    <IconComponent className="w-5 h-5 text-[#C41E3A]" />
                    <h3 className="font-semibold text-gray-800 dark:text-white">{label}</h3>
                    <span className="text-sm text-gray-400">({features.length} items)</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50/50 dark:bg-gray-900/50">
                        <tr>
                          <th className="text-left py-2 px-4 font-medium text-gray-600 dark:text-gray-300 text-sm">Feature Name</th>
                          <th className="text-left py-2 px-4 font-medium text-gray-600 dark:text-gray-300 text-sm">ID</th>
                          <th className="text-left py-2 px-4 font-medium text-gray-600 dark:text-gray-300 text-sm">Description</th>
                          <th className="text-right py-2 px-4 font-medium text-gray-600 dark:text-gray-300 text-sm">
                            Price ($){category === 'upgrade' ? '/sqft' : ''}
                          </th>
                          <th className="text-center py-2 px-4 font-medium text-gray-600 dark:text-gray-300 text-sm w-16">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {features.map((feature) => (
                          <tr key={feature.feature_id} className="border-t dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="py-2 px-4">
                              <Input
                                value={feature.feature_name}
                                onChange={(e) => handleFeatureChange(feature.originalIndex, 'feature_name', e.target.value)}
                                className="text-sm"
                              />
                            </td>
                            <td className="py-2 px-4">
                              <span className="text-xs text-gray-400 font-mono">{feature.feature_id}</span>
                            </td>
                            <td className="py-2 px-4">
                              <Input
                                value={feature.description || ''}
                                onChange={(e) => handleFeatureChange(feature.originalIndex, 'description', e.target.value)}
                                className="text-sm"
                                placeholder="Description"
                              />
                            </td>
                            <td className="py-2 px-4">
                              <Input
                                type="number"
                                step="0.01"
                                value={feature.price}
                                onChange={(e) => handleFeatureChange(feature.originalIndex, 'price', e.target.value)}
                                className="text-right w-24 ml-auto text-sm"
                              />
                            </td>
                            <td className="py-2 px-4 text-center">
                              <button
                                onClick={() => removeFeature(feature.originalIndex)}
                                className="p-1 text-red-500 hover:bg-red-50 rounded"
                                title="Remove"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                        {features.length === 0 && (
                          <tr>
                            <td colSpan={5} className="py-4 text-center text-gray-400">
                              No {label.toLowerCase()} configured
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
            
            <Button
              variant="outline"
              onClick={addFeature}
              className="flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Custom Feature
            </Button>
          </div>
        )}

        {/* Rates Tab */}
        {activeTab === 'rates' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <Percent className="w-5 h-5 text-[#C41E3A]" />
                Discount, Tax & Deposit Rates
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                These rates are used to calculate the final building estimate shown to salespeople.
              </p>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <label className="block text-sm font-semibold text-green-800 mb-2">
                    Manufacturer Discount (%)
                  </label>
                  <p className="text-xs text-green-600 mb-3">
                    Discount applied to the base building estimate
                  </p>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={rates.manufacturer_discount_percent}
                      onChange={(e) => handleRateChange('manufacturer_discount_percent', e.target.value)}
                      className="text-lg font-semibold text-center"
                    />
                    <span className="text-lg font-semibold text-green-700">%</span>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <label className="block text-sm font-semibold text-blue-800 mb-2">
                    Sales Tax (%)
                  </label>
                  <p className="text-xs text-blue-600 mb-3">
                    Tax applied to the subtotal after discount
                  </p>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={rates.sales_tax_percent}
                      onChange={(e) => handleRateChange('sales_tax_percent', e.target.value)}
                      className="text-lg font-semibold text-center"
                    />
                    <span className="text-lg font-semibold text-blue-700">%</span>
                  </div>
                </div>

                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <label className="block text-sm font-semibold text-purple-800 mb-2">
                    Deposit Required (%)
                  </label>
                  <p className="text-xs text-purple-600 mb-3">
                    Percentage of total required as deposit
                  </p>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={rates.deposit_percent}
                      onChange={(e) => handleRateChange('deposit_percent', e.target.value)}
                      className="text-lg font-semibold text-center"
                    />
                    <span className="text-lg font-semibold text-purple-700">%</span>
                  </div>
                </div>
              </div>

              {/* Preview calculation */}
              <div className="bg-gray-50 border rounded-lg p-4 mt-6">
                <h4 className="font-semibold text-gray-700 mb-3">Example Calculation (for $10,000 building)</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-300">Building Estimate:</span>
                    <span className="font-medium">$10,000.00</span>
                  </div>
                  <div className="flex justify-between text-green-600">
                    <span>Manufacturer Discount ({rates.manufacturer_discount_percent}%):</span>
                    <span className="font-medium">-${(10000 * rates.manufacturer_discount_percent / 100).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="text-gray-600 dark:text-gray-300">Subtotal:</span>
                    <span className="font-medium">${(10000 * (1 - rates.manufacturer_discount_percent / 100)).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-blue-600">
                    <span>Sales Tax ({rates.sales_tax_percent}%):</span>
                    <span className="font-medium">${(10000 * (1 - rates.manufacturer_discount_percent / 100) * rates.sales_tax_percent / 100).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2 text-lg font-bold text-[#C41E3A]">
                    <span>Total:</span>
                    <span>${(10000 * (1 - rates.manufacturer_discount_percent / 100) * (1 + rates.sales_tax_percent / 100)).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2 mt-2">
                    <span className="text-purple-600">Deposit Amount ({rates.deposit_percent}%):</span>
                    <span className="font-medium text-purple-600">${(10000 * (1 - rates.manufacturer_discount_percent / 100) * (1 + rates.sales_tax_percent / 100) * rates.deposit_percent / 100).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-300">Due Upon Delivery:</span>
                    <span className="font-medium">${(10000 * (1 - rates.manufacturer_discount_percent / 100) * (1 + rates.sales_tax_percent / 100) * (1 - rates.deposit_percent / 100)).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default PricingPage;
